import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Zap, BarChart3, Search, Shield, Users, Eye, Mail, Send, CheckCircle } from 'lucide-react';
import TrialRegistrationForm from '../components/TrialRegistrationForm';

// Système de log détaillé pour production
console.log('🟢 HomePage.tsx - Module chargé');
console.log('🔍 Environment variables check:', {
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ? '✓ Définie' : '✗ Manquante',
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? '✓ Définie' : '✗ Manquante',
  VITE_APP_URL: import.meta.env.VITE_APP_URL ? '✓ Définie' : '✗ Manquante',
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD
});

// Composant d'indicateur de progression de lecture
const ReadingProgressIndicator: React.FC = () => {
  const [progress, setProgress] = useState(0);

  React.useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', updateProgress);
    return () => window.removeEventListener('scroll', updateProgress);
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
      <div 
        className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// Interface pour le formulaire de contact
interface ContactForm {
  prenom: string;
  nom: string;
  entreprise: string;
  email: string;
  message?: string;
}

/**
 * HomePage - Page d'accueil standard
 * Explique l'outil et propose un formulaire de contact
 */
export const HomePage: React.FC = () => {
  console.log('🟢 HomePage - Composant en cours de montage');
  
  const [contactForm, setContactForm] = useState<ContactForm>({
    prenom: '',
    nom: '',
    entreprise: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Log de cycle de vie du composant
  useEffect(() => {
    console.log('✅ HomePage - Composant monté avec succès');
    console.log('📍 Location:', window.location.href);
    console.log('🌐 User Agent:', navigator.userAgent);
    
    return () => {
      console.log('🔴 HomePage - Composant démonté');
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Utilisation de Netlify Forms avec AJAX
      const myForm = e.target as HTMLFormElement;
      const formData = new FormData(myForm);
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        console.log('✅ Email envoyé avec succès via Netlify Forms');
        setIsSubmitted(true);
        
        // Reset form après 3 secondes
        setTimeout(() => {
          setIsSubmitted(false);
          setContactForm({
            prenom: '',
            nom: '',
            entreprise: '',
            email: '',
            message: ''
          });
        }, 3000);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi:', error);
      alert('Une erreur est survenue lors de l\'envoi. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = contactForm.prenom && contactForm.nom && contactForm.entreprise && contactForm.email;

  return (
    <div className="min-h-screen bg-white">
      {/* Indicateur de progression de lecture */}
      <ReadingProgressIndicator />
      
      {/* Header optimisé mobile */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40 backdrop-blur-sm bg-white/95">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-center">
            <div className="flex flex-col items-center sm:flex-row sm:items-center sm:space-x-3 space-y-1 sm:space-y-0">
              <div className="flex-shrink-0">
                <img src="/images/smart_impact_logo-no-bg.png" alt="Smart Impact" className="h-10 object-contain" />
              </div>
              <div className="text-center">
                <p className="text-gray-600 text-xs font-medium">
                  Agence digitale • Référencement • IA
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section - Optimisé mobile */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 md:py-20 lg:py-24 overflow-hidden">
        {/* Background Elements - Réduits pour mobile */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-64 h-64 md:w-96 md:h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-48 h-48 md:w-80 md:h-80 bg-orange-400/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            
            {/* Titre optimisé mobile */}
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-6 leading-tight text-white">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                Audit Digital Gratuit
              </span>
              <br />
              <span className="text-white">
                pour votre entreprise
              </span>
            </h1>
                
            {/* Description de l'outil */}
            <div className="mx-auto mb-8 max-w-3xl text-center">
              <p className="mb-4 text-lg md:text-xl text-gray-300 leading-relaxed">
                Notre outil d'analyse digitale examine votre site web sous tous les angles : 
                <span className="text-orange-400 font-semibold"> performance, SEO, réputation en ligne</span> et 
                <span className="text-orange-400 font-semibold"> opportunités de croissance</span>.
              </p>
              <p className="mb-6 text-lg md:text-xl text-gray-300 leading-relaxed">
                En quelques minutes, obtenez un diagnostic complet avec des recommandations personnalisées 
                pour améliorer votre présence digitale et attirer plus de clients.
              </p>
            </div>
            
            {/* Badge de confiance */}
            <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
              <div className="inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm font-medium text-gray-300">
                <Star className="mr-2" size={16} />
                +400 entreprises analysées
              </div>
              <div className="inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm font-medium text-gray-300">
                <Shield className="mr-2" size={16} />
                100% gratuit
              </div>
              <div className="inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm font-medium text-gray-300">
                <Zap className="mr-2" size={16} />
                Résultats en 2 minutes
              </div>
            </div>
            
            {/* CTA Principal - Supprimé selon la demande */}
          </div>
        </div>
      </section>
      
      {/* Section Essai Gratuit - Déplacée avant la section Token Email */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                <Zap className="w-4 h-4 mr-2" />
                Démarrer votre audit
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Obtenez votre audit digital gratuit en 2 minutes
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Entrez l'URL de votre site web et votre email professionnel pour recevoir instantanément votre diagnostic complet
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <TrialRegistrationForm />
            </div>
          </div>
        </div>
      </section>
      
      {/* Section Token Email */}
      <section className="bg-orange-50 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              className="bg-white rounded-2xl shadow-lg border border-orange-200 p-6 md:p-8 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                <Mail className="w-4 h-4 mr-2" />
                Accès personnalisé
              </div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Vous avez reçu un lien personnalisé ?
              </h2>
              
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Si vous êtes arrivé sur cette page, vous avez probablement reçu un email avec un 
                <span className="font-semibold text-orange-600"> token spécifique</span> pour accéder à l'analyse 
                de votre propre site web. Ce lien vous donne accès à un rapport personnalisé déjà préparé pour votre entreprise.
              </p>
              
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Format du lien :</span> 
                  <span className="font-mono bg-white px-2 py-1 rounded ml-2">
                    get-smart.ch/analyse/[votre-token-unique]
                  </span>
                </p>
              </div>
              
              <p className="text-gray-600">
                Si vous n'avez pas encore reçu votre lien personnalisé, contactez-nous via le formulaire ci-dessous 
                ou commencez directement votre audit gratuit.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Avantages */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                <Eye className="w-4 h-4 mr-2" />
                Ce que vous obtenez
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Un diagnostic complet de votre présence digitale
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Notre analyse couvre tous les aspects essentiels de votre performance en ligne
              </p>
            </div>

            {/* Grille des avantages */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Performance */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 w-fit mb-4">
                  <Zap size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Performance & Vitesse</h3>
                <p className="text-gray-600 mb-4">
                  Analyse complète de la vitesse de chargement sur mobile et desktop avec recommandations d'optimisation.
                </p>
                <div className="text-sm text-orange-600 font-medium">
                  ✓ Temps de chargement
                  <br />
                  ✓ Core Web Vitals
                  <br />
                  ✓ Optimisations techniques
                </div>
              </motion.div>

              {/* SEO */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 w-fit mb-4">
                  <Search size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Référencement SEO</h3>
                <p className="text-gray-600 mb-4">
                  Positionnement dans Google, analyse des mots-clés et opportunités de croissance identifiées.
                </p>
                <div className="text-sm text-orange-600 font-medium">
                  ✓ Positions Google
                  <br />
                  ✓ Mots-clés détectés
                  <br />
                  ✓ Potentiel de trafic
                </div>
              </motion.div>

              {/* Réputation */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 w-fit mb-4">
                  <Star size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Réputation en ligne</h3>
                <p className="text-gray-600 mb-4">
                  Analyse des avis Google et de votre e-réputation avec stratégies d'amélioration.
                </p>
                <div className="text-sm text-orange-600 font-medium">
                  ✓ Avis Google
                  <br />
                  ✓ Note moyenne
                  <br />
                  ✓ Gestion réputation
                </div>
              </motion.div>

              {/* Analyse technique */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 w-fit mb-4">
                  <BarChart3 size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Analyse Technique</h3>
                <p className="text-gray-600 mb-4">
                  Détection du CMS, technologies utilisées et recommandations d'amélioration technique.
                </p>
                <div className="text-sm text-orange-600 font-medium">
                  ✓ CMS détecté
                  <br />
                  ✓ Technologies
                  <br />
                  ✓ Sécurité
                </div>
              </motion.div>

              {/* Recommandations IA */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 w-fit mb-4">
                  <Star size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Recommandations IA</h3>
                <p className="text-gray-600 mb-4">
                  Plan d'action personnalisé généré par IA avec priorités et étapes concrètes.
                </p>
                <div className="text-sm text-orange-600 font-medium">
                  ✓ Plan personnalisé
                  <br />
                  ✓ Actions prioritaires
                  <br />
                  ✓ ROI estimé
                </div>
              </motion.div>

              {/* Support expert */}
              <motion.div 
                className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 w-fit mb-4">
                  <Users size={24} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Support Expert</h3>
                <p className="text-gray-600 mb-4">
                  Accompagnement par nos experts digitaux pour interpréter les résultats et passer à l'action.
                </p>
                <div className="text-sm text-orange-600 font-medium">
                  ✓ Consultation gratuite
                  <br />
                  ✓ Experts certifiés
                  <br />
                  ✓ Suivi personnalisé
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </section>

      {/* Section Formulaire de Contact */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            <motion.div 
              className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {/* Header */}
              <div className="p-6 md:p-8 text-center">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-4">
                  <Mail className="w-4 h-4 mr-2" />
                  Nous contacter
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Besoin d'aide ou d'informations ?
                </h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Notre équipe d'experts est là pour répondre à vos questions et vous accompagner 
                  dans votre transformation digitale.
                </p>
              </div>

              {/* Formulaire */}
              <div className="p-6 md:p-8 pt-0">
                {isSubmitted ? (
                  <motion.div 
                    className="text-center py-8"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Message envoyé !</h4>
                    <p className="text-gray-600">
                      Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
                    </p>
                  </motion.div>
                ) : (
                  <form 
                    name="contact" 
                    method="POST" 
                    data-netlify="true" 
                    netlify-honeypot="bot-field"
                    onSubmit={handleSubmit} 
                    className="space-y-6"
                  >
                    {/* Hidden fields for Netlify Forms */}
                    <input type="hidden" name="form-name" value="contact" />
                    <div hidden>
                      <input name="bot-field" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">
                          Prénom *
                        </label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          value={contactForm.prenom}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                          placeholder="Votre prénom"
                        />
                      </div>
                      <div>
                        <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                          Nom *
                        </label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          value={contactForm.nom}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                          placeholder="Votre nom"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="entreprise" className="block text-sm font-medium text-gray-700 mb-2">
                        Entreprise *
                      </label>
                      <input
                        type="text"
                        id="entreprise"
                        name="entreprise"
                        value={contactForm.entreprise}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="Nom de votre entreprise"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="votre.email@entreprise.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                        Message (optionnel)
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={contactForm.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                        placeholder="Décrivez votre projet ou vos besoins..."
                      />
                    </div>

                    <div className="text-center">
                      <motion.button
                        type="submit"
                        disabled={!isFormValid || isSubmitting}
                        className={`inline-flex items-center px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                          isFormValid && !isSubmitting
                            ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                        whileHover={isFormValid && !isSubmitting ? { scale: 1.05 } : {}}
                        whileTap={isFormValid && !isSubmitting ? { scale: 0.98 } : {}}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <Send className="mr-3" size={20} />
                            Envoyer le message
                          </>
                        )}
                      </motion.button>
                      
                      <p className="text-sm text-gray-500 mt-3">
                        * Champs obligatoires
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Essai Gratuit - Déjà déplacée plus haut */}
    </div>
  );
};

export default HomePage;
